import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type {
  RiskLibraryCategory,
  RiskLibraryCompanyHeader,
  RiskLibraryTaskDetail,
  RiskLibraryTaskSummary,
} from '../../core/_models'
import { niceEnum } from './riskLibraryTaskDetailDisplay'

const CATEGORY_HEADER_BG: Record<string, string> = {
  DECK_ROUTINE: '#2f5f9e',
  DECK_NON_ROUTINE: '#6b5b95',
  ENGINE_ROUTINE: '#2d7a4a',
  ENGINE_NON_ROUTINE: '#5a9a6e',
  CRITICAL_EQUIPMENT: '#c78b2b',
}

function categoryHeaderRgb(code: string): [number, number, number] {
  const hex = (CATEGORY_HEADER_BG[code] || '#5c6b7a').replace('#', '')
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)]
}

function slugFilePart(s: string) {
  return (s || 'export').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 64) || 'export'
}

function bulletBlock(lines: string[]): string {
  const t = lines.map((x) => x.trim()).filter(Boolean)
  if (!t.length) return '—'
  return t.map((x) => `• ${x}`).join('\n')
}

function addCompanyAndSafHeader(
  doc: jsPDF,
  margin: number,
  pageWidth: number,
  companyName: string,
  header: RiskLibraryCompanyHeader
): number {
  const revision = header.revision != null && header.revision !== '' ? String(header.revision) : '—'
  const body: string[][] = [
    ['Company', companyName || '—'],
    ['Document', header.document || '—'],
    ['Valid from', header.validFrom ? String(header.validFrom).slice(0, 10) : '—'],
    ['Revision', revision],
    ['Process', header.process || '—'],
    ['Issuer', header.issuer || '—'],
    ['Procedure', header.procedure || '—'],
  ]

  autoTable(doc, {
    startY: margin,
    body,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak', valign: 'top' },
    columnStyles: {
      0: { cellWidth: 100, fontStyle: 'bold', fillColor: [248, 249, 250] },
      1: { cellWidth: pageWidth - 2 * margin - 100 },
    },
    margin: { left: margin, right: margin },
  })
  const finalY = (doc as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY
  return (typeof finalY === 'number' ? finalY : margin + 120) + 18
}

export function exportRiskLibraryMasterListPdf(opts: {
  companyName: string
  libraryHeader: RiskLibraryCompanyHeader
  categories: RiskLibraryCategory[]
  matrixByCat: Record<number, RiskLibraryTaskSummary[]>
}): void {
  const { companyName, libraryHeader, categories, matrixByCat } = opts
  const margin = 36
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' })
  const pageW = doc.internal.pageSize.getWidth()

  let y = addCompanyAndSafHeader(doc, margin, pageW, companyName, libraryHeader)
  doc.setFontSize(13)
  doc.setFont('helvetica', 'bold')
  doc.text('SAF-015 Risk assessments master list', margin, y)
  y += 22

  const cols = categories.map((c) => matrixByCat[c.id] || [])
  const maxLen = Math.max(0, ...cols.map((t) => t.length))
  const matrixRows = Array.from({ length: maxLen }, (_, rowIdx) =>
    categories.map((c) => {
      const list = matrixByCat[c.id] || []
      return list[rowIdx] ?? null
    })
  )

  const headRow1 = categories.map((c) => ({
    content: c.name,
    colSpan: 2,
    styles: {
      fillColor: categoryHeaderRgb(c.code),
      textColor: 255,
      halign: 'center' as const,
      valign: 'middle' as const,
      fontStyle: 'bold' as const,
    },
  }))

  const headRow2 = categories.flatMap(() => [
    { content: 'Task ID', styles: { fillColor: [236, 239, 241], fontStyle: 'bold' as const, halign: 'center' as const } },
    { content: 'Task', styles: { fillColor: [236, 239, 241], fontStyle: 'bold' as const, halign: 'center' as const } },
  ])

  const body = matrixRows.map((row) =>
    row.flatMap((cell) => [cell ? String(cell.categoryTaskNumber) : '—', cell ? cell.title : ''])
  )

  const tableW = pageW - 2 * margin
  const pairW = tableW / Math.max(categories.length, 1) / 2
  const columnStyles: Record<number, { cellWidth: number }> = {}
  categories.forEach((_, ci) => {
    columnStyles[ci * 2] = { cellWidth: Math.max(36, pairW * 0.28) }
    columnStyles[ci * 2 + 1] = { cellWidth: Math.max(80, pairW * 0.72) }
  })

  autoTable(doc, {
    startY: y,
    head: [headRow1 as any, headRow2 as any],
    body,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', valign: 'top', lineColor: [120, 120, 120], lineWidth: 0.2 },
    margin: { left: margin, right: margin },
    tableWidth: tableW,
    columnStyles,
  })

  const stamp = new Date().toISOString().slice(0, 10)
  doc.save(`SAF-015_master-list_${slugFilePart(companyName)}_${stamp}.pdf`)
}

function flattenCategoryRows(categoryCode: string, tasks: RiskLibraryTaskDetail[]): string[][] {
  const rows: string[][] = []
  for (const task of tasks) {
    if (categoryCode === 'CRITICAL_EQUIPMENT') {
      const failures =
        task.failures && task.failures.length > 0
          ? task.failures
          : [
              {
                failureText: '—',
                likelihood: 'POSSIBLE' as const,
                severity: 'MINOR' as const,
                riskFactor: 'LOW' as const,
                hazardPoints: [{ pointText: '—' }],
                existingControls: [] as { lineText: string }[],
                furtherControls: [] as { lineText: string }[],
              },
            ]
      const equip = task.equipmentName || task.title || '—'
      for (const f of failures) {
        const hp = bulletBlock((f.hazardPoints || []).map((p) => p.pointText))
        const ex = bulletBlock((f.existingControls || []).map((c) => c.lineText))
        const fu = bulletBlock((f.furtherControls || []).map((c) => c.lineText))
        rows.push([
          String(task.categoryTaskNumber),
          equip,
          f.failureText || '—',
          hp,
          ex,
          `Likelihood: ${niceEnum(f.likelihood)}`,
          `Severity: ${niceEnum(f.severity)}`,
          `Risk: ${niceEnum(f.riskFactor)}`,
          fu,
        ])
      }
    } else {
      const hazards = task.hazards?.length ? task.hazards : []
      const hRows =
        hazards.length > 0
          ? hazards
          : [
              {
                hazardText: '—',
                likelihood: 'POSSIBLE' as const,
                severity: 'MINOR' as const,
                riskFactor: 'LOW' as const,
                existingControls: [] as { lineText: string }[],
                furtherControls: [] as { lineText: string }[],
              },
            ]
      const work = task.workActivity || task.title || '—'
      for (const h of hRows) {
        const ex = bulletBlock((h.existingControls || []).map((c) => c.lineText))
        const fu = bulletBlock((h.furtherControls || []).map((c) => c.lineText))
        rows.push([
          String(task.categoryTaskNumber),
          work,
          h.hazardText || '—',
          ex,
          `Likelihood: ${niceEnum(h.likelihood)}`,
          `Severity: ${niceEnum(h.severity)}`,
          `Risk: ${niceEnum(h.riskFactor)}`,
          fu,
        ])
      }
    }
  }
  return rows
}

export function exportRiskLibraryCategoryWorksheetPdf(opts: {
  companyName: string
  libraryHeader: RiskLibraryCompanyHeader
  categoryName: string
  categoryCode: string
  sheetTasks: RiskLibraryTaskDetail[]
}): void {
  const { companyName, libraryHeader, categoryName, categoryCode, sheetTasks } = opts
  const margin = 32
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' })
  const pageW = doc.internal.pageSize.getWidth()

  let y = addCompanyAndSafHeader(doc, margin, pageW, companyName, libraryHeader)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text(`Risk assessment worksheet — ${categoryName}`, margin, y)
  y += 16
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`Tasks exported: ${sheetTasks.length}  ·  Version column (list): see task detail in app`, margin, y)
  y += 20

  const isCe = categoryCode === 'CRITICAL_EQUIPMENT'
  const head = isCe
    ? [
        [
          'Task ID',
          'Equipment',
          'Failure',
          'Hazard points',
          'Existing control measures',
          'Likelihood',
          'Severity',
          'Risk factor',
          'Further controls',
        ],
      ]
    : [
        [
          'Task ID',
          'Work activity',
          'Hazard',
          'Existing control measures',
          'Likelihood',
          'Severity',
          'Risk factor',
          'Further controls',
        ],
      ]

  const body = flattenCategoryRows(categoryCode, sheetTasks)

  const tableW = pageW - 2 * margin
  const columnStyles: Record<number, { cellWidth: number }> = isCe
    ? {
        0: { cellWidth: tableW * 0.06 },
        1: { cellWidth: tableW * 0.12 },
        2: { cellWidth: tableW * 0.12 },
        3: { cellWidth: tableW * 0.16 },
        4: { cellWidth: tableW * 0.16 },
        5: { cellWidth: tableW * 0.1 },
        6: { cellWidth: tableW * 0.1 },
        7: { cellWidth: tableW * 0.09 },
        8: { cellWidth: tableW * 0.09 },
      }
    : {
        0: { cellWidth: tableW * 0.07 },
        1: { cellWidth: tableW * 0.14 },
        2: { cellWidth: tableW * 0.14 },
        3: { cellWidth: tableW * 0.2 },
        4: { cellWidth: tableW * 0.11 },
        5: { cellWidth: tableW * 0.11 },
        6: { cellWidth: tableW * 0.11 },
        7: { cellWidth: tableW * 0.12 },
      }

  const headRgb = categoryHeaderRgb(categoryCode)

  autoTable(doc, {
    startY: y,
    head,
    body,
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [100, 100, 100],
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: headRgb,
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
    },
    alternateRowStyles: { fillColor: [252, 253, 255] },
    margin: { left: margin, right: margin },
    tableWidth: tableW,
    columnStyles,
  })

  const stamp = new Date().toISOString().slice(0, 10)
  const catSlug = slugFilePart(categoryName)
  doc.save(`risk-assessment_worksheet_${catSlug}_${slugFilePart(companyName)}_${stamp}.pdf`)
}
